import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet';
import '../css/class-by-age.css';
import {
    DAY_ORDER,
    fetchClasses,
    flagList,
    flagLabel,
    statusLabel,
    statusBadgeKind,
    formatTimeRange,
    formatAgeRange,
} from '../utils/scheduleData';
import { fetchContent } from '../utils/siteContent';

const AGE_BUCKETS = [
    { id: 'toddler', label: '18mo – 3', min: 0, max: 3 },
    { id: '3-5', label: '3 – 5', min: 3, max: 5 },
    { id: '5-7', label: '5 – 7', min: 5, max: 7 },
    { id: '8-12', label: '8 – 12', min: 8, max: 12 },
    { id: '12-17', label: '12 – 17', min: 12, max: 17 },
    { id: 'adult', label: 'Adult', adultOnly: true },
];

function matchesBucket(cls, bucket) {
    if (bucket.adultOnly) return flagList(cls.flags).includes('ADULT');
    return cls.age_max >= bucket.min && cls.age_min <= bucket.max;
}

function dayOrderIndex(cls) {
    const first = cls._days[0];
    const idx = DAY_ORDER.indexOf(first);
    return idx === -1 ? DAY_ORDER.length : idx;
}

export default function ClassbyAge() {
    const [classes, setClasses] = useState(null);
    const [error, setError] = useState(null);
    const [activeBucket, setActiveBucket] = useState('all');
    const [content, setContent] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchClasses('fall')
            .then((data) => { if (!cancelled) setClasses(data); })
            .catch((err) => { if (!cancelled) setError(err.message); });
        fetchContent().then((data) => { if (!cancelled) setContent(data); });
        return () => { cancelled = true; };
    }, []);

    const sortedClasses = useMemo(() => {
        if (!classes) return [];
        return [...classes].sort((a, b) => dayOrderIndex(a) - dayOrderIndex(b) || a._startMin - b._startMin);
    }, [classes]);

    // "All Ages" shows one flat list. A single bucket is a real filter down
    // to that age range. We deliberately don't render every bucket's section
    // at once — many classes span multiple buckets (e.g. Skill Level classes
    // list as 8-99), so stacking all sections would repeat the same class
    // several times.
    const visibleClasses = useMemo(() => {
        if (activeBucket === 'all') return sortedClasses;
        const bucket = AGE_BUCKETS.find((b) => b.id === activeBucket);
        return sortedClasses.filter((c) => matchesBucket(c, bucket));
    }, [sortedClasses, activeBucket]);

    return (
        <div className="classByAge-content">
            <Helmet>
                <title>In Motion Dance | Classes By Age</title>
            </Helmet>
            <h1>Classes by Age</h1>
            <p className="classByAge-subtitle">Fall 2026 – June 2027</p>
            {content && content.schedule_updated && (
                <p className="classByAge-updated">Updated: {content.schedule_updated}</p>
            )}

            {error && (
                <p className="classByAge-error">
                    We couldn't load the class list ({error}). Please refresh, or contact the
                    studio if this keeps happening.
                </p>
            )}

            {!error && !classes && <p className="classByAge-loading">Loading classes…</p>}

            {classes && (
                <>
                    <div className="classByAge-chips">
                        <button
                            className={`chip ${activeBucket === 'all' ? 'chip-active' : ''}`}
                            onClick={() => setActiveBucket('all')}
                        >
                            All Ages
                        </button>
                        {AGE_BUCKETS.map(({ id, label }) => (
                            <button
                                key={id}
                                className={`chip ${activeBucket === id ? 'chip-active' : ''}`}
                                onClick={() => setActiveBucket(id)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="classByAge-list">
                        {visibleClasses.map((cls) => (
                            <div className="ageCard" key={`${cls.class_id}-${cls._days.join('')}`}>
                                <div className="ageCard-main">
                                    <span className="ageCard-name">{cls.name}</span>
                                    <span className="ageCard-schedule">{cls.days} · {formatTimeRange(cls)}</span>
                                </div>
                                <div className="ageCard-meta">
                                    <span className="ageCard-age">{formatAgeRange(cls)}</span>
                                    {cls.instructor && <span className="ageCard-instructor">{cls.instructor}</span>}
                                </div>
                                {statusBadgeKind(cls.status) || flagList(cls.flags).length > 0 ? (
                                    <div className="ageCard-badges">
                                        {statusBadgeKind(cls.status) && (
                                            <span className={`badge badge-status-${statusBadgeKind(cls.status)}`}>
                                                {statusLabel(cls.status)}
                                            </span>
                                        )}
                                        {flagList(cls.flags).map((flag) => (
                                            <span className="badge badge-flag" key={flag}>{flagLabel(flag)}</span>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}
